import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Modal from '@lib/components/Modal/Modal';
import Button from '@lib/components/Button/Button';
import { PATH_LATEST_VERSION } from '@lib/enums/urls';
import useIsOnline from '@lib/hooks/useIsOnline';
import { logger } from '@lib/services/logger';
import styles from './ReleaseTracker.module.scss';

type Props = {
  captureException: (error: Error) => void;
  domain: string;
  releaseVersion: string;
  releaseTrackerSeconds: string | number;
  mode: string;
};

export default function ReleaseTracker(props: Props) {
  const {
    captureException,
    releaseTrackerSeconds,
    releaseVersion,
    domain,
    mode,
  } = props;
  const { t } = useTranslation();
  const [lastReleaseVersion, setLastReleaseVersion] = useState<string | null>(
    null,
  );
  const { pathname, search } = useLocation();
  const lastFetchTimeRef = useRef<Date | null>(null);
  const isOnline = useIsOnline();

  const isModalAvailable = !!releaseVersion && !!lastReleaseVersion;
  const isModalOpen = releaseVersion !== lastReleaseVersion;

  useEffect(() => {
    const fetchFileContent = async () => {
      try {
        lastFetchTimeRef.current = new Date(); // Update last fetch time
        const response = await fetch(`${domain}/${PATH_LATEST_VERSION}.txt`, {
          cache: 'reload',
        });
        if (!response.ok) return;
        const content = await response.text();
        const contentValue = content.trim();
        logger.debug('ReleaseTracker: version check', {
          lastVersion: contentValue,
          releaseVersion,
        });
        if (contentValue) setLastReleaseVersion(contentValue);
      } catch (error) {
        captureException(
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    };

    const shouldFetch = ((): boolean => {
      if (!domain || mode === 'development' || !releaseTrackerSeconds)
        return false;
      if (!lastFetchTimeRef.current) return true;
      const now = new Date();
      const diff = now.getTime() - lastFetchTimeRef.current.getTime();
      const seconds =
        typeof releaseTrackerSeconds === 'string'
          ? parseInt(releaseTrackerSeconds, 10) * 1000
          : releaseTrackerSeconds * 1000;
      return diff >= seconds;
    })();

    if (shouldFetch && isOnline) {
      fetchFileContent();
    }
  }, [
    captureException,
    domain,
    isOnline,
    mode,
    pathname,
    releaseTrackerSeconds,
    releaseVersion,
    search,
  ]);

  if (!isModalAvailable) return null;

  const reload = async () => {
    const url = new URL(window.location.href);
    url.searchParams.set('ts', String(Date.now()));

    // Force fetch fresh index.html to update browser HTTP cache
    try {
      await fetch(url.origin + url.pathname, { cache: 'reload' });
    } catch (error) {
      logger.warn('ReleaseTracker: failed to prefetch index.html', {
        error: error instanceof Error ? error.message : String(error),
      });
    }

    // Clear navigation cache so SW fetches fresh index.html on reload.
    try {
      await caches.delete('navigations');
    } catch {
      // ignore
    }

    window.location.replace(url.toString());
  };

  const modalActions = (
    <div className={styles.actions}>
      <Button
        key="refresh"
        leftIcon="refresh"
        buttonText={t('refresh-page')}
        onClick={reload}
      />
    </div>
  );

  return (
    <Modal
      isOpen={isModalAvailable && isModalOpen}
      title={t('release-tracker-title')}
      description={t('release-tracker-desc')}
      actions={modalActions}
    />
  );
}
